import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import axios from 'axios';
import classnames from 'classnames';
import { Link } from 'react-router-dom';

import Sidebar from 'react-sidebar';
import MediaQuery from 'react-responsive';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/free-solid-svg-icons';

import DesktopHeader from './DesktopHeader';
import DesktopFooter from './footer/DesktopFooter';

import SidebarContent from '../common/sidebar/SidebarContent';
import useSidebar from '../common/sidebar/useSidebar';
import { getDesktopSidebarStyles } from '../../utils/sidebar-utils';

import UserProfileCourseList from '../profile/courses/UserProfileCourseList';
import WebinarBlock from '../profile/blocks/WebinarBlock';
import TimetableBlock from '../profile/blocks/TimetableBlock';

import ProfileTabContent from '../profile/tabs/ProfileTabContent';
import CollapsableBlock from '../profile/blocks/CollapsableBlock';

import UserSettingsBlockContent from '../profile/blocks/settings/UserSettingsBlockContent';
import ChangePasswordForm from '../profile/blocks/settings/ChangePasswordForm';

import PaymentsTable from '../profile/payments/PaymentsTable';
import PaymentsTabContentMobile from '../mobile/profile/tabs/PaymentsTabContentMobile';

import { checkUserAuth } from '../../utils/auth-utils';

import Loader from '../common/loader/Loader';
import Alert from '../common/Alert';

const tabs = ['My courses', 'My profile', 'Payments'];

const defaultTimetable = [
  { month: 'Sun', times: [] },
  { month: 'Mon', times: [] },
  { month: 'Tue', times: [] },
  { month: 'Wed', times: [] },
  { month: 'Thu', times: [] },
  { month: 'Fri', times: [] },
  { month: 'Sat', times: [] },
];

const defaultWebinar = {
  name: 'Coming soon',
  description: 'We are working to prepare the best webinar for you',
  date: 'Date is undefined',
  price: 0,
};

const ProfilePageDesktop = props => {
  const { user, token } = useSelector(state => state);

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const [courses, setCourses] = useState([]);
  const [timetable, setTimetable] = useState(defaultTimetable);
  const [payments, setPayments] = useState([]);
  const [webinar, setWebinar] = useState(defaultWebinar);
  const [status, setStatus] = useState({ isLoading: false, isError: false });

  useEffect(() => {
    setStatus({ isLoading: true, isError: false });
    axios
      .get('/api/v1/profile', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(response => {
        setPayments(response.data.payments);
        setCourses(response.data.courses);
        setTimetable(response.data.timetable);

        if (response.data.webinar) {
          setWebinar(response.data.webinar);
        }

        setStatus({ isLoading: false, isError: false });
      })
      .catch(e => {
        setStatus({ isLoading: false, isError: true });
        checkUserAuth(e, props.history);
      });
  }, []);

  const getClickTabHandler = index => e => {
    e.preventDefault();
    setSelectedTabIndex(index);
  };

  const { isSidebarOpen, setIsSidebarOpen, openSidebar, closeSidebar } = useSidebar();

  return (
    <Sidebar
      open={isSidebarOpen}
      onSetOpen={setIsSidebarOpen}
      sidebar={<SidebarContent closeSidebar={closeSidebar} />}
      styles={getDesktopSidebarStyles()}
    >
      <DesktopHeader openSidebar={openSidebar} />
      <Loader isVisible={status.isLoading} />
      <div className="profile-header">
        <div className="tutorium-container">
          {status.isError && <Alert isError message="An error occured" />}
          <div className="is-flex align-items-center py-20px">
            <img
              src="img/icons/user.svg"
              style={{ width: '50px', height: '50px', minWidth: '50px', minHeight: '50px' }}
            />
            <p className="profile-header-user-name ml-20px">{user.name}</p>
          </div>
          <div className="is-flex">
            {tabs.map((tabName, tabIndex) => (
              <a
                key={tabName}
                className={classnames('profile-header-tab-link', { active: tabIndex === selectedTabIndex })}
                onClick={getClickTabHandler(tabIndex)}
              >
                {tabName}
              </a>
            ))}
            {!!user.isAdmin && (
              <Link to="/admin" className="button is-link ml-20px">
                <FontAwesomeIcon icon={faCog} />
                <span className="ml-10px">Admin Panel</span>
              </Link>
            )}
          </div>
        </div>

        <div className="profile-container">
          <div className="tutorium-container">
            <div className="profile-content">
              <ProfileTabContent isVisible={selectedTabIndex === 0}>
                {courses.length === 0 && (
                  <div className="profile-coming-soon">
                    <p>You do not have any courses yet.</p>
                  </div>
                )}
                {courses.length > 0 && <UserProfileCourseList courses={courses} />}
                <div>
                  <WebinarBlock webinar={webinar} />
                  <TimetableBlock timetable={timetable} />
                </div>
              </ProfileTabContent>
              <ProfileTabContent isVisible={selectedTabIndex === 1}>
                <div className="user-courses-container">
                  <CollapsableBlock title="Profile settings" isOpen={true}>
                    <UserSettingsBlockContent />
                  </CollapsableBlock>
                  <CollapsableBlock title="Password settings">
                    <ChangePasswordForm />
                  </CollapsableBlock>
                </div>
                <div>
                  <WebinarBlock webinar={webinar} />
                </div>
              </ProfileTabContent>
              <ProfileTabContent isVisible={selectedTabIndex === 2}>
                <div className="user-courses-container">
                  <MediaQuery minWidth={1024}>
                    <PaymentsTable payments={payments} />
                  </MediaQuery>
                  <MediaQuery maxWidth={1023}>
                    <PaymentsTabContentMobile payments={payments} />
                  </MediaQuery>
                </div>
              </ProfileTabContent>
            </div>
          </div>
        </div>
      </div>
      <DesktopFooter />
    </Sidebar>
  );
};

ProfilePageDesktop.propTypes = {
  history: PropTypes.object.isRequired,
};

export default ProfilePageDesktop;
